import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
    selector: 'input[appDateMask]',
    standalone: true
})
export class DateMaskDirective {
    /** Maximum number of digits per segment: month, day, year. */
    private static readonly SEGMENT_MAX = [2, 2, 4];

    private _formatting = false;

    constructor(private el: ElementRef<HTMLInputElement>) {}

    @HostListener('input', ['$event'])
    // Typed as Event: Angular types the 'input' host listener's $event as Event, and the
    // re-dispatched event below is a plain Event with no inputType.
    onInput(event: Event): void {
        if (this._formatting) return;

        const input = this.el.nativeElement;
        const inputType = (event as InputEvent).inputType;
        const isDeleting =
            inputType === 'deleteContentBackward' ||
            inputType === 'deleteContentForward';

        const formatted = this._format(input.value, isDeleting);

        if (formatted === input.value) return;

        this._formatting = true;
        input.value = formatted;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        this._formatting = false;
    }

    /**
     * Normalizes free-form date entry to MM/DD/YYYY.
     *
     * Walks the raw value character by character, distributing digits across the
     * month/day/year segments. A segment advances to the next either when it fills
     * its fixed width (2/2/4) — so digit-only entry like "01012002" auto-inserts
     * both slashes — or when the user types a "/" delimiter, which also pads a
     * single-digit month/day with a leading zero (so "1/1/2000" -> "01/01/2000").
     */
    private _format(value: string, isDeleting: boolean): string {
        const segments = ['', '', ''];
        let seg = 0;

        for (const char of value) {
            if (seg > 2) break;

            if (char >= '0' && char <= '9') {
                const max = DateMaskDirective.SEGMENT_MAX[seg];
                if (segments[seg].length < max) {
                    segments[seg] += char;
                }
                if (seg < 2 && segments[seg].length >= max) {
                    seg++;
                }
            } else if (char === '/' && segments[seg].length > 0) {
                if (seg < 2) {
                    segments[seg] = this._pad(segments[seg]);
                }
                seg++;
            }
        }

        let formatted = segments[0];
        if (segments[1] !== '') formatted += `/${segments[1]}`;
        if (segments[2] !== '') formatted += `/${segments[2]}`;

        // Preserve a trailing delimiter the user just typed so they can keep
        // typing into the next segment, but never re-add one while deleting and
        // never produce a leading "/" before any segment has content.
        if (
            !isDeleting &&
            formatted.length > 0 &&
            value.endsWith('/') &&
            !formatted.endsWith('/')
        ) {
            formatted += '/';
        }

        return formatted;
    }

    private _pad(segment: string): string {
        return segment.length === 1 ? `0${segment}` : segment.substring(0, 2);
    }
}
